import clsx from 'clsx';
import Image from 'components/ui/Image';
import config from 'config';
import { useTranslation } from 'hooks/useTranslation';
import styles from './BeforeAfter.module.scss';
import type { APIBeforeAfterPhoto } from 'types/BeforeAfterPhotos';

type ItemProps = {
  slide: APIBeforeAfterPhoto;
  isGrid?: boolean;
  isMobile?: boolean;
  isThumbnail?: boolean;
  locale?: string;
  variant: string;
};

const BeforeAfterItem = ({
  slide,
  isGrid,
  isMobile,
  isThumbnail,
  locale,
  variant,
}: ItemProps): JSX.Element => {
  const { aftermedia, beforeafterphoto, beforemedia } = slide;
  const { imageFallbackSize } = config.defaults;

  const additionalProps = isThumbnail
    ? {
        fill: true,
      }
    : {
        height: beforemedia?.height || imageFallbackSize,
        width: beforemedia?.width || imageFallbackSize,
      };

  return (
    <div>
      <div className="flex items-center justify-center gap-2">
        <div
          className={clsx(
            'flex items-center overflow-hidden no-inline-block-image',
            !isGrid &&
              !isThumbnail &&
              !isMobile &&
              'gap-2 rounded-md md:w-8/12 justify-center',
            isThumbnail && 'h-10 w-full',
          )}
          style={{
            border: isGrid ? '1px solid #EEF2F6' : '',
          }}
        >
          {locale === 'de' ? null : (
            <div
              className={clsx(
                'w-1/2 relative text-right rounded-tl-md rounded-bl-md overflow-hidden',
                !isGrid &&
                  !isThumbnail &&
                  !isMobile &&
                  'flex items-center justify-end',
                isThumbnail ? 'h-10' : 'h-auto',
              )}
            >
              {/** eslint-disable-next-line
               * @ts-ignore */}
              <Image
                className={clsx(
                  'self-center w-full object-contain',
                  isGrid && 'bg-bluishGrey',
                  isThumbnail ? 'object-cover' : 'object-contain',
                )}
                src={beforemedia?.url as string}
                alt={beforemedia?.name || ''}
                contentful
                {...additionalProps}
              />
            </div>
          )}
          <div
            className={clsx(
              'relative overflow-hidden',
              locale === 'de' && beforeafterphoto?.omit_before_picture
                ? 'w-full text-center rounded-md justify-center'
                : 'w-1/2 rounded-tr-md rounded-br-md',
              !isGrid && !isThumbnail && !isMobile && 'flex items-center',
              isThumbnail ? 'h-10' : 'h-auto',
            )}
          >
            {/** eslint-disable-next-line
             * @ts-ignore */}
            <Image
              className={clsx(
                'self-center w-full object-contain',
                isGrid && 'bg-bluishGrey',
              )}
              src={aftermedia?.url as string}
              alt={aftermedia?.name || ''}
              contentful
              {...additionalProps}
            />
          </div>
        </div>
      </div>

      {!isGrid ? (
        <p
          className={clsx(
            'text-center pt-1 px-4 md:hidden',
            variant !== 'alternate' && 'text-white',
          )}
          dangerouslySetInnerHTML={{
            __html: beforeafterphoto?.description || '',
          }}
        />
      ) : null}
    </div>
  );
};

type Props = {
  isGrid?: boolean;
  isMobile?: boolean;
  onImageClick: (i: number) => void;
  slides: APIBeforeAfterPhoto[];
  variant: string;
};

const BeforeAfter = ({
  isGrid,
  onImageClick,
  slides,
  variant,
}: Props): JSX.Element => {
  const { locale } = useTranslation();

  if (isGrid) {
    return (
      <div
        className={clsx(
          'gap-3 box-border mx-auto space-y-4',
          styles['masonry-2-col'],
          locale === 'de'
            ? styles['lg:masonry-4-col']
            : styles['lg:masonry-3-col'],
        )}
      >
        {slides.map((slide, i) => (
          <div
            key={i}
            className={clsx(
              'flex items-center w-full cursor-pointer no-inline-block-image rounded-md overflow-hidden',
              styles['break-inside'],
            )}
            onClick={() => {
              onImageClick(i);
            }}
          >
            <BeforeAfterItem
              slide={slide}
              isGrid
              locale={locale}
              variant={variant}
            />
          </div>
        ))}
      </div>
    );
  }

  return <></>;
};

BeforeAfter.Item = BeforeAfterItem;
export default BeforeAfter;
